import { useMemo, useState } from 'react';
import { Badge, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useContractsLandList } from '../../../queries/contracts-land';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { ContractFormDrawer } from './ContractFormDrawer';
import type { ContractLand } from '../../../types/api';

export function ContractListPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';

  const [q, setQ] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ContractLand | null>(null);

  const filters = useMemo(() => ({ q: q.trim() || undefined, limit: 200 }), [q]);
  const { data: contracts = [], isLoading } = useContractsLandList(filters);
  const { data: members } = useProjectMembers(projectId);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Contract</Title>
        <Text c="dimmed">Pick a project to view its contracts.</Text>
      </Stack>
    );
  }

  function openCreate() {
    setEditTarget(null);
    setDrawerOpen(true);
  }

  function openEdit(c: ContractLand) {
    setEditTarget(c);
    setDrawerOpen(true);
  }

  function lastUpdatedLabel(c: ContractLand): string {
    const name =
      members?.find((m) => m.user_id === c.updated_by_user_id)?.username ??
      c.updated_by_user_id ??
      '—';
    return `${name} · ${new Date(c.updated_at).toLocaleDateString()}`;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Contract</Title>
          <Text c="dimmed" size="sm">
            Project-scoped contracts (JOAs, farmouts, unit agreements, and similar instruments).
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New contract
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search contract #, title, parties"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={contracts}
        idAccessor="id"
        noRecordsText="No contracts yet"
        onRowClick={({ record }) => openEdit(record)}
        columns={[
          {
            accessor: 'contract_number',
            title: 'Contract #',
            render: (c) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {c.contract_number}
                </Text>
              </Stack>
            ),
          },
          {
            accessor: 'contract_title',
            title: 'Title',
            render: (c) => <Text size="sm">{c.contract_title ?? '—'}</Text>,
          },
          {
            accessor: 'contract_type',
            title: 'Type',
            width: 160,
            render: (c) =>
              c.contract_type ? (
                <Badge variant="light" color="indigo">
                  {c.contract_type}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              ),
          },
          {
            accessor: 'party_1',
            title: 'Parties',
            render: (c) => (
              <Stack gap={0}>
                <Text size="sm">{c.party_1 ?? '—'}</Text>
                {c.other_parties && (
                  <Text size="xs" c="dimmed">
                    {c.other_parties}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'updated_at',
            title: 'Last updated',
            render: (c) => (
              <Text size="xs" c="dimmed">
                {lastUpdatedLabel(c)}
              </Text>
            ),
          },
        ]}
      />

      <ContractFormDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditTarget(null);
        }}
        contract={editTarget}
      />
    </Stack>
  );
}
